import React, { useState } from "react";
import "./Search.css";
import gearCards from "../All Cards/GearCards";
import glossary from "../All Cards/Glossary";
import abilities from "../All Cards/Abilities";
import disorders from "../All Cards/Disorders";
import fightingArts from "../All Cards/FightingArts";
import impairments from "../All Cards/Impairments";
import secretFightingArts from "../All Cards/SecretFightingArts";
import strainFightingArts from "../All Cards/StrainFightingArts";
import weaponProficiencies from "../All Cards/WeaponProficiency";
import knowledge from "../All Cards/Knowledge";
import philosophy from "../All Cards/Philosophy";
import settlementEvents from "../All Cards/SettlementEvents";
import scoutDiscovery from "../All Cards/ScoutDiscovery";
import innovation from "../All Cards/Innovation";
import resources from "../All Cards/Resources";
import { renderItem } from "./CardRenderer";

const Search = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");

  const dataMapping = {
    gear: gearCards.map((item) => ({ ...item, cardType: "gear" })),
    glossary: glossary.map((item) => ({ ...item, cardType: "glossary" })),
    abilities: abilities.map((item) => ({ ...item, cardType: "abilities" })),
    disorders: disorders.map((item) => ({ ...item, cardType: "disorders" })),
    fightingArts: fightingArts.map((item) => ({ ...item, cardType: "fightingArts" })),
    impairments: impairments.map((item) => ({ ...item, cardType: "impairments" })),
    secretFightingArts: secretFightingArts.map((item) => ({ ...item, cardType: "secretFightingArts" })),
    strainFightingArts: strainFightingArts.map((item) => ({ ...item, cardType: "strainFightingArts" })),
    weaponProficiencies: weaponProficiencies.map((item) => ({ ...item, cardType: "weaponProficiencies" })),
    knowledge: knowledge.map((item) => ({ ...item, cardType: "knowledge" })),
    philosophy: philosophy.map((item) => ({ ...item, cardType: "philosophy" })),

    settlementEvents: settlementEvents.map((item) => ({ ...item, cardType: "settlementEvents" })),
    scoutDiscovery: scoutDiscovery.map((item) => ({ ...item, cardType: "scoutDiscovery" })),

    innovation: innovation.map((item) => ({ ...item, cardType: "innovation" })),
    resources: resources.map((item) => ({ ...item, cardType: "resources" })),
  };

  const optionList = [
    { value: "abilities", label: "Abilities" },
    { value: "disorders", label: "Disorders" },
    { value: "fightingArts", label: "Fighting Arts" },
    { value: "gear", label: "Gear Cards" },
    { value: "glossary", label: "Glossary" },
    { value: "impairments", label: "Impairments" },
    { value: "innovation", label: "Innovations" },
    { value: "knowledge", label: "Knowledge" },
    { value: "philosophy", label: "Philosophy" },
    { value: "resources", label: "Resources" },
    { value: "scoutDiscovery", label: "Scout Discovery" },
    { value: "secretFightingArts", label: "Secret Fighting Arts" },
    { value: "settlementEvents", label: "Settlement Events" },
    { value: "strainFightingArts", label: "Strain Fighting Arts" },
    { value: "weaponProficiencies", label: "Weapon Proficiencies" },
  ];

  const sortedOptions = [
    { value: "all", label: "All" },
    ...optionList.sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
  };

  const getFilteredResults = () => {
    const data =
      selectedType === "all"
        ? Object.values(dataMapping).flat()
        : dataMapping[selectedType] || [];

    const searchLower = searchTerm.trim().toLowerCase();

    // Handle "Keyword: XYZ" searches (gear cards only)
    if (searchLower.startsWith("keyword:")) {
      const keyword = searchLower.replace("keyword:", "").trim();

      const filteredGear = gearCards
        .filter(
          (item) =>
            item.keywords &&
            typeof item.keywords === "string" &&
            item.keywords.toLowerCase().includes(keyword)
        )
        .map((item) => ({ ...item, cardType: "gear" }));

      // ✅ Alphabetical (Gear uses name)
      return filteredGear.sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, {
          sensitivity: "base",
        })
      );
    }

    // General search across all items
    const filtered = data.filter((item) =>
      Object.values(item).some(
        (value) => typeof value === "string" && value.toLowerCase().includes(searchLower)
      )
    );

    // ✅ Alphabetical for ALL card types:
    // Gear/most cards use `name`, Glossary/Abilities/Impairments/Disorders use `term`.
    return filtered.sort((a, b) => {
      const aKey = String(a.name ?? a.term ?? "").trim();
      const bKey = String(b.name ?? b.term ?? "").trim();
      return aKey.localeCompare(bKey, undefined, { sensitivity: "base" });
    });
  };

  return (
    <div className="compendium-container">
      <h2>Compendium</h2>

      <input
        type="text"
        placeholder="Search..."
        value={searchTerm}
        onChange={handleSearch}
      />

      <select onChange={handleTypeChange} value={selectedType}>
        {sortedOptions.map((option, index) => (
          <option key={index} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <div className="results">
        {getFilteredResults().map((item, index) => (
          <div
            key={index}
            className={item.cardType === "gear" ? "gear-card" : "glossary-entry"}
          >
            <div className="card-content">
              {selectedType === "all"
                ? renderItem(item)
                : item.cardType === "gear"
                ? renderItem(item)
                : renderContent(item, selectedType)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Search;
